import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Plus, Minus, Trash2, ArrowLeft, UserPlus, ImagePlus, X, Loader2 } from "lucide-react";
import { api, fileUrl, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { rupiah } from "@/lib/format";
import { Card, Button, EmptyState, Input, Modal } from "@/components/common";
import CustomerSearch from "@/components/CustomerSearch";
import QrisPaymentModal from "@/components/QrisPaymentModal";

function pad(n) { return String(n).padStart(2, "0"); }
function quickDate(days, hour = 10) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function parseLocalDateTime(s) {
  if (!s || !s.trim()) return null;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
  if (!m) return undefined; // invalid
  const [, y, mo, d, h, mi] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi)).toISOString();
}
const dtDate = (s) => (s && s.includes(" ") ? s.split(" ")[0] : (s || ""));
const dtTime = (s) => (s && s.includes(" ") ? s.split(" ")[1] : "");
const composeDT = (d, t) => (d ? `${d} ${t || "10:00"}` : "");

function DateTimeField({ label, value, onChange, testid }) {
  return (
    <div>
      <span className="text-sm font-medium text-slate-600 mb-1 block">{label}</span>
      <div className="flex gap-2">
        <input type="date" value={dtDate(value)} onChange={(e) => onChange(composeDT(e.target.value, dtTime(value)))} className="flex-1 h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid={`${testid}-date`} />
        <input type="time" value={dtTime(value)} onChange={(e) => onChange(composeDT(dtDate(value), e.target.value))} className="w-32 h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid={`${testid}-time`} />
      </div>
    </div>
  );
}

function QuickCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    setBusy(true);
    try {
      const c = await api.post("/customers", { name: form.name.trim(), phone: form.phone.trim(), address: form.address.trim() });
      toast.success("Pelanggan ditambahkan");
      onCreated(c);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Tambah Pelanggan Cepat" maxWidth="max-w-sm">
      <div className="space-y-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama pelanggan" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="qc-name" autoFocus />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Nomor WhatsApp" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="qc-phone" />
        <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Alamat (opsional)" rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" data-testid="qc-address" />
        <Button className="w-full" onClick={save} disabled={busy} data-testid="qc-save">{busy ? "Menyimpan..." : "Simpan Pelanggan"}</Button>
      </div>
    </Modal>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 h-8 rounded-full text-xs font-medium whitespace-nowrap border ${active ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
      {label}
    </button>
  );
}

function PhotoAttach({ label, hint, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Ukuran foto maksimal 8MB"); return; }
    setBusy(true);
    try { const up = await uploadImage(file); onChange(up.id); }
    catch (err) { toast.error(err.message || "Gagal mengunggah foto"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div>
      <span className="text-sm font-medium text-slate-600 block">{label}</span>
      {hint && <span className="text-xs text-slate-400 mb-1.5 block">{hint}</span>}
      {value ? (
        <div className="relative inline-block mt-1">
          <img src={fileUrl(value)} alt={label} className="w-28 h-28 rounded-xl object-cover border border-slate-200" />
          <button type="button" onClick={() => onChange(null)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center shadow" data-testid={`${testid}-remove`}><X size={13} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="w-28 h-28 mt-1 rounded-xl border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-400 hover:border-primary hover:text-primary disabled:opacity-60" data-testid={testid}>
          {busy ? <Loader2 className="animate-spin" size={20} /> : <ImagePlus size={22} />}
          <span className="text-xs">{busy ? "Mengunggah..." : "Pilih File"}</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
    </div>
  );
}

export default function Checkout() {
  const { branch } = useAuth();
  const cart = useCart();
  const nav = useNavigate();
  const { draft, setDraft, lines } = cart;

  const [discount, setDiscount] = useState("0");
  const [discType, setDiscType] = useState("rp");
  const [quickOpen, setQuickOpen] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState("0");
  const [methods, setMethods] = useState([{ code: "cash", name: "Tunai" }]);
  const [method, setMethod] = useState("cash");
  const [payAmount, setPayAmount] = useState("");
  const [reference, setReference] = useState("");
  const [err, setErr] = useState(null);
  const [shortages, setShortages] = useState([]);
  const [needPin, setNeedPin] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [qrisOrder, setQrisOrder] = useState(null);
  const [requestPhoto, setRequestPhoto] = useState(null);
  const [wrapPhoto, setWrapPhoto] = useState(null);

  const isDelivery = !!(draft.delivery_address || "").trim();
  const selMethod = methods.find((m) => (m.code || m.id) === method);
  const isQris = selMethod?.type === "qris" || /qris/i.test(method || "");

  useEffect(() => {
    api.get("/payment-methods").then((l) => { if (l?.length) { setMethods(l); setMethod(l[0].code || l[0].id); } }).catch(() => {});
  }, []);

  const discInput = Number(discount) || 0;
  const disc = Math.min(discType === "pct" ? Math.round((cart.subtotal * discInput) / 100) : discInput, cart.subtotal);
  const fee = isDelivery ? Number(deliveryFee) || 0 : 0;
  const salesTotal = Math.max(cart.subtotal - disc, 0);
  const total = salesTotal + fee;
  const pay = payAmount === "" ? (draft.is_preorder ? 0 : total) : Number(payAmount) || 0;
  const remaining = Math.max(total - pay, 0);

  const submit = async () => {
    if (!branch) return toast.error("Pilih cabang dulu");
    setErr(null); setShortages([]);
    let due = null;
    if (draft.is_preorder || (draft.due_date || "").trim()) {
      due = parseLocalDateTime(draft.due_date);
      if (due === undefined) { setErr("Format tanggal & jam ambil/kirim: YYYY-MM-DD HH:mm"); return; }
      if (draft.is_preorder && pay <= 0) { setErr("Pre-order wajib ada DP"); return; }
    }
    if (pay > total) { setErr("Pembayaran melebihi total"); return; }
    if (isQris && draft.is_preorder && pay <= 0) { setErr("Pre-order wajib ada DP"); return; }
    setBusy(true);
    try {
      const order = await api.post("/orders", {
        branch_id: branch.id,
        order_type: draft.is_preorder ? "preorder" : draft.source_text ? "whatsapp" : "pos",
        customer_name: draft.customer_name,
        customer_phone: draft.customer_phone,
        delivery_address: draft.delivery_address,
        due_date: due,
        notes: draft.notes,
        discount: disc,
        delivery_fee: fee,
        fulfillment: isDelivery ? "delivery" : "pickup",
        items: lines.map((l) => ({ product_id: l.product_id, variant_id: l.variant_id, qty: l.qty, price: l.price, note: l.note || "" })),
        // QRIS: payment is confirmed by Midtrans settlement, so don't record it upfront.
        payments: isQris ? [] : (pay > 0 ? [{ method, amount: pay, reference }] : []),
        source_text: draft.source_text,
        request_photo_file_id: requestPhoto,
        wrap_photo_file_id: wrapPhoto,
        manager_pin: needPin && pin ? pin : undefined,
      });
      if (isQris) {
        setQrisOrder(order);
        return;
      }
      toast.success(`Order ${order.order_no} berhasil dibuat`);
      cart.clear();
      nav("/order-center");
    } catch (e) {
      if (e.status === 409 && e.data?.detail?.shortages) { setShortages(e.data.detail.shortages); setNeedPin(true); }
      setErr(e.data?.detail?.message || e.message || "Gagal membuat order");
    } finally {
      setBusy(false);
    }
  };

  if (lines.length === 0) {
    return (
      <EmptyState icon={ShoppingCart} title="Keranjang kosong" message="Belum ada produk untuk di-checkout."
        action={<Button onClick={() => nav("/pos")} data-testid="checkout-back-empty"><ArrowLeft size={16} /> Kembali ke Kasir</Button>} />
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => nav("/pos")} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50" data-testid="checkout-back"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Checkout</h1>
          <p className="text-sm text-slate-500">{branch?.name}</p>
        </div>
        <button onClick={() => { cart.clear(); nav("/pos"); }} className="ml-auto inline-flex items-center gap-1.5 text-sm text-rose-600 hover:text-rose-700" data-testid="checkout-clear"><Trash2 size={15} /> Kosongkan</button>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="space-y-6">
          {/* ITEM LIST */}
          <Card className="overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2"><ShoppingCart size={17} /> Daftar Item ({cart.count})</h2>
            </div>
            <div data-testid="checkout-items">
              {lines.map((l) => (
                <div key={l.key} className="flex items-center gap-3 px-4 py-3 border-b border-slate-50 last:border-0" data-testid={`checkout-item-${l.key}`}>
                  <div className="w-14 h-14 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                    {l.product?.photo_file_id || l.product?.image_url ? (
                      <img src={l.product.photo_file_id ? fileUrl(l.product.photo_file_id) : l.product.image_url} alt={l.name} className="w-full h-full object-cover" />
                    ) : <span className="text-xl">💐</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{l.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{rupiah(l.price)} <span className="text-slate-300">/ satuan</span> × {l.qty}</div>
                    <div className="text-sm font-semibold text-primary mt-0.5" data-testid={`checkout-subtotal-${l.key}`}>{rupiah(l.price * l.qty)}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => cart.setQty(l.key, l.qty - 1)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:border-primary" data-testid={`checkout-dec-${l.key}`}><Minus size={14} /></button>
                    <span className="w-7 text-center text-sm font-medium" data-testid={`checkout-qty-${l.key}`}>{l.qty}</span>
                    <button onClick={() => cart.setQty(l.key, l.qty + 1)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:border-primary" data-testid={`checkout-inc-${l.key}`}><Plus size={14} /></button>
                    <button onClick={() => cart.setQty(l.key, 0)} className="w-8 h-8 rounded-lg text-rose-500 flex items-center justify-center hover:bg-rose-50" data-testid={`checkout-remove-${l.key}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => nav("/pos")} className="w-full flex items-center justify-center gap-2 h-11 border-t border-dashed border-primary/40 text-primary text-sm font-medium hover:bg-primary/5" data-testid="checkout-add-product">
              <Plus size={16} /> Tambah Produk
            </button>
          </Card>

          {/* CUSTOMER */}
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Pelanggan</h3>
              <button onClick={() => setQuickOpen(true)} className="inline-flex items-center gap-1.5 text-sm text-primary font-medium" data-testid="checkout-quick-customer"><UserPlus size={15} /> Pelanggan Baru</button>
            </div>
            <CustomerSearch value={draft.customer_name} onChangeName={(name) => setDraft({ customer_name: name })}
              onPick={(c) => setDraft({ customer_name: c.name || "", customer_phone: c.phone || "", delivery_address: c.address || draft.delivery_address })}
              placeholder="Cari / ketik nama pelanggan (opsional)" testid="checkout-customer-name" />
            <input value={draft.customer_phone} onChange={(e) => setDraft({ customer_phone: e.target.value })} placeholder="No. WhatsApp" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="checkout-customer-phone" />
            <textarea value={draft.delivery_address} onChange={(e) => setDraft({ delivery_address: e.target.value })} placeholder="Alamat kirim (kosongkan jika ambil di toko)" rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" data-testid="checkout-address" />
            {isDelivery && (
              <Input label="Ongkos kirim (Rp) — dibayar customer, dicatat terpisah dari omset" type="number" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)} data-testid="checkout-delivery-fee" />
            )}
            {!draft.is_preorder && (
              <div>
                <DateTimeField label={isDelivery ? "Tanggal & jam kirim (opsional)" : "Tanggal & jam ambil di toko (opsional)"} value={draft.due_date} onChange={(v) => setDraft({ due_date: v })} testid="checkout-pickup-time" />
                <div className="flex gap-2 mt-2">
                  <Chip label="Hari ini" onClick={() => setDraft({ due_date: quickDate(0) })} />
                  <Chip label="Besok" onClick={() => setDraft({ due_date: quickDate(1) })} />
                  <Chip label="Kosongkan" onClick={() => setDraft({ due_date: "" })} />
                </div>
              </div>
            )}
            <textarea value={draft.notes} onChange={(e) => setDraft({ notes: e.target.value })} placeholder="Catatan / kartu ucapan" rows={2} className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm" data-testid="checkout-notes" />
          </Card>

          {/* FOTO LAMPIRAN */}
          <Card className="p-5 space-y-3">
            <h3 className="font-semibold text-slate-800">Lampiran Foto (opsional)</h3>
            <div className="flex flex-wrap gap-6">
              <PhotoAttach label="Foto Request Order" hint="Referensi pesanan pelanggan" value={requestPhoto} onChange={setRequestPhoto} testid="checkout-request-photo" />
              <PhotoAttach label="Foto Warna Bungkus Buket" hint="Warna bungkus buket" value={wrapPhoto} onChange={setWrapPhoto} testid="checkout-wrap-photo" />
            </div>
          </Card>

          {/* ORDER STATUS: Reguler / Pre-Order */}
          <Card className="p-5">
            <span className="text-sm font-medium text-slate-600 mb-2 block">Status Pesanan</span>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setDraft({ is_preorder: false })} data-testid="order-status-regular"
                className={`h-11 rounded-xl text-sm font-medium border ${!draft.is_preorder ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300"}`}>Reguler</button>
              <button onClick={() => setDraft({ is_preorder: true, due_date: draft.due_date || quickDate(1) })} data-testid="order-status-preorder"
                className={`h-11 rounded-xl text-sm font-medium border ${draft.is_preorder ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-300"}`}>Pre-Order (PO)</button>
            </div>
            {draft.is_preorder && (
              <div className="mt-4">
                <p className="text-xs text-slate-400 mb-2">Stok direservasi, dipotong saat mulai dirangkai. Wajib ada DP.</p>
                <DateTimeField label="Tanggal & jam ambil/kirim" value={draft.due_date} onChange={(v) => setDraft({ due_date: v })} testid="checkout-due-date" />
                <div className="flex gap-2 mt-2">
                  <Chip label="Besok" onClick={() => setDraft({ due_date: quickDate(1) })} />
                  <Chip label="Lusa" onClick={() => setDraft({ due_date: quickDate(2) })} />
                  <Chip label="+7 hari" onClick={() => setDraft({ due_date: quickDate(7) })} />
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* PAYMENT + SUMMARY */}
        <Card className="p-5 space-y-4 lg:sticky lg:top-4">
          <h3 className="font-semibold text-slate-800">Pembayaran</h3>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-slate-600">Diskon</span>
              <div className="flex rounded-lg overflow-hidden border border-slate-300">
                <button onClick={() => setDiscType("rp")} className={`px-3 h-7 text-xs font-medium ${discType === "rp" ? "bg-primary text-white" : "bg-white text-slate-500"}`} data-testid="disc-type-rp">Rp</button>
                <button onClick={() => setDiscType("pct")} className={`px-3 h-7 text-xs font-medium ${discType === "pct" ? "bg-primary text-white" : "bg-white text-slate-500"}`} data-testid="disc-type-pct">%</button>
              </div>
            </div>
            <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="checkout-discount" placeholder={discType === "pct" ? "0 %" : "Rp 0"} />
            {discType === "pct" && disc > 0 && <div className="text-xs text-slate-400 mt-1">= {rupiah(disc)}</div>}
          </div>
          <div>
            <span className="text-sm font-medium text-slate-600 mb-1.5 block">Metode</span>
            <div className="flex flex-wrap gap-2">
              {methods.map((m) => (
                <Chip key={m.code || m.id} label={m.name} active={method === (m.code || m.id)} onClick={() => setMethod(m.code || m.id)} testid={`pay-method-${m.code || m.id}`} />
              ))}
            </div>
          </div>
          <div>
            <span className="text-sm font-medium text-slate-600 mb-1.5 block">Status Pembayaran</span>
            <div className="flex flex-wrap gap-2">
              <Chip label="Lunas" active={pay >= total && total > 0} onClick={() => setPayAmount(String(total))} testid="pay-full" />
              <Chip label="DP (Uang Muka)" active={pay > 0 && pay < total} onClick={() => setPayAmount(String(Math.round(total / 2)))} testid="pay-half" />
              {!draft.is_preorder && <Chip label="Belum Bayar" active={pay <= 0} onClick={() => setPayAmount("0")} testid="pay-none" />}
            </div>
          </div>
          <Input label={draft.is_preorder ? "Jumlah DP (Rp)" : "Jumlah bayar (Rp)"} type="number" value={payAmount === "" ? String(pay) : payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="checkout-pay-amount" />
          {method !== "cash" && (
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="No. referensi (opsional)" className="w-full h-10 px-3 rounded-xl border border-slate-300 text-sm" data-testid="checkout-reference" />
          )}

          {shortages.length > 0 && (
            <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm" data-testid="checkout-shortages">
              <div className="font-medium text-rose-700">Stok bahan tidak cukup di {branch?.name}</div>
              {shortages.map((sh) => (
                <div key={sh.item_id} className="text-rose-600 text-xs mt-1">• {sh.name}: butuh {sh.needed}, tersedia {sh.available}</div>
              ))}
              {needPin && (
                <div className="mt-2">
                  <div className="text-xs text-rose-600 mb-1">Kebijakan stok minus: penjualan boleh lanjut dengan persetujuan PIN Manager.</div>
                  <input value={pin} onChange={(e) => setPin(e.target.value)} type="password" inputMode="numeric" maxLength={8} placeholder="PIN Manager" className="w-full h-10 px-3 rounded-xl border border-rose-300 text-sm" data-testid="checkout-pin" />
                </div>
              )}
            </div>
          )}
          {err && <div className="text-sm text-rose-600" data-testid="checkout-error">{err}</div>}

          <div className="border-t border-slate-100 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span data-testid="checkout-sum-subtotal">{rupiah(cart.subtotal)}</span></div>
            {disc > 0 && <div className="flex justify-between"><span className="text-slate-500">Diskon</span><span className="text-rose-600">- {rupiah(disc)}</span></div>}
            {fee > 0 && <div className="flex justify-between"><span className="text-slate-500">Ongkos kirim</span><span>{rupiah(fee)}</span></div>}
            <div className="flex justify-between text-base font-semibold text-slate-800"><span>Total bayar</span><span data-testid="checkout-sum-total">{rupiah(total)}</span></div>
            {remaining > 0 && <div className="flex justify-between"><span className="text-slate-500">Sisa tagihan (Piutang)</span><span className="text-amber-600" data-testid="checkout-sum-remaining">{rupiah(remaining)}</span></div>}
          </div>

          <Button className="w-full" size="lg" onClick={submit} disabled={busy || lines.length === 0} data-testid="checkout-submit">
            {busy ? "Memproses..." : isQris ? `Tampilkan QRIS · ${rupiah(total)}` : draft.is_preorder ? `Simpan Pre-Order · DP ${rupiah(pay)}` : `Bayar ${rupiah(pay)}`}
          </Button>
        </Card>
      </div>

      {quickOpen && (
        <QuickCustomerModal onClose={() => setQuickOpen(false)} onCreated={(c) => {
          setDraft({ customer_name: c.name || "", customer_phone: c.phone || "", delivery_address: c.address || draft.delivery_address });
          setQuickOpen(false);
        }} />
      )}

      {qrisOrder && (
        <QrisPaymentModal order={qrisOrder} method={method}
          onPaid={() => { toast.success(`Pembayaran QRIS ${qrisOrder.order_no} berhasil`); cart.clear(); setQrisOrder(null); nav("/order-center"); }}
          onClose={() => { toast.message(`Order ${qrisOrder.order_no} dibuat (menunggu pembayaran QRIS)`); cart.clear(); setQrisOrder(null); nav("/order-center"); }} />
      )}
    </div>
  );
}
